import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import gradeService from "../../services/gradeService";
import semesterService from "../../services/semesterService";
import enrollmentService from "../../services/enrollmentService";
import classService from "../../services/classService";
import { cache, cacheKeys } from "../../utils/cacheUtils";
import { calculateAverage, calculateGradesAverage, calculateOverallAverage } from "../../utils/gradeCalculations";
// import { roundToTwoDecimals } from "../../utils/mathUtils";
import { getCurrentSemester } from "../../utils/dateUtils";
import { getGradeColor } from "../../utils/gradeColors";
import LoadingSpinner from "../common/LoadingSpinner";
import GradesChart from "./GradesChart";

const AnalyticsDashboard = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester] = useState("ALL");
  const [selectedSubjectFilter] = useState("ALL");

  // Chart specific semester filter
  const [selectedSemesters, setSelectedSemesters] = useState([]);
  const [isSemesterFilterOpen, setIsSemesterFilterOpen] = useState(false);

  // For Custom Average Calculation
  const [customSelectedSubjects, setCustomSelectedSubjects] = useState([]);
  const [subjectSearchTerm, setSubjectSearchTerm] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        setLoading(true);

        const fetchWithCache = async (key, fetchFn) => {
          const cached = cache.get(key);
          if (cached) return cached;
          const data = await fetchFn();
          cache.set(key, data);
          return data;
        };

        const [gradesData, allSubjectsData, semestersData, myClassesData] =
          await Promise.all([
            fetchWithCache(cacheKeys.grades(user.id), () => gradeService.getGradesByUser(user.id)),
            fetchWithCache(cacheKeys.allSubjects(), () => enrollmentService.getSubjects()),
            fetchWithCache(cacheKeys.allSemesters(), () => semesterService.getAllSemesters()),
            fetchWithCache(cacheKeys.myClasses(user.id), () => classService.getMyClasses().catch(() => ({ content: [] }))),
          ]);

        const classes = myClassesData?.content || myClassesData || [];

        // Only semesters from user classes
        const userClassIds = new Set(classes.map((c) => c.id));
        const userSemesters = semestersData.filter((s) => userClassIds.has(s.classId));

        const enrichedSemesters = userSemesters.map((sem) => {
          const classInfo = classes.find((c) => c.id === sem.classId);
          return {
            ...sem,
            classInfo,
            className: classInfo ? classInfo.name : null,
            schoolName:
              sem.school?.name ||
              classInfo?.schoolName ||
              (sem.schoolId ? `School ${sem.schoolId}` : "General"),
          };
        });

        const sortedSemesters = enrichedSemesters.sort((a, b) => a.id - b.id);
        setSemesters(sortedSemesters);

        // Only subjects that appear in grades
        const gradeSubjectIds = new Set(gradesData.map((g) => String(g.subjectId)));
        const relevantSubjects = allSubjectsData.filter((s) =>
          gradeSubjectIds.has(String(s.id))
        );

        // Format subject names with code if available and assign school
        const formattedSubjects = relevantSubjects.map(s => {
          // Find a grade for this subject to determine the semester and thus the school
          const grade = gradesData.find(g => String(g.subjectId) === String(s.id));
          let schoolName = "General";
          
          if (grade) {
            const semesterId = grade.semesterId || (typeof grade.semester === 'object' ? grade.semester.id : null);
            if (semesterId) {
              const semester = enrichedSemesters.find(sem => sem.id === semesterId);
              if (semester) {
                schoolName = semester.schoolName;
              }
            }
          }

          return {
            ...s,
            name: s.subjectCode ? `${s.subjectCode} - ${s.name}` : s.name,
            schoolName
          };
        });

        setGrades(gradesData);
        setSubjects(formattedSubjects);

        // Default to current semesters if found, otherwise last 2
        const currentSemesterIds = [];
        classes.forEach((cls) => {
          if (!cls.startDate) return;
          const currentSemInfo = getCurrentSemester(cls.startDate);
          if (!currentSemInfo?.semesterNumber) return;

          const matchingSem = sortedSemesters.find(
            (s) =>
              s.classId === cls.id &&
              parseInt(s.name?.match(/\d+/)?.[0] || "0") === currentSemInfo.semesterNumber
          );
          if (matchingSem) currentSemesterIds.push(matchingSem.id);
        });

        if (currentSemesterIds.length > 0) {
          setSelectedSemesters(currentSemesterIds);
        } else {
          setSelectedSemesters(sortedSemesters.slice(-2).map((s) => s.id));
        }
      } catch (error) {
        console.error("Error fetching analytics data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const toggleSemester = (semesterId) => {
    setSelectedSemesters((prev) =>
      prev.includes(semesterId) ? prev.filter((id) => id !== semesterId) : [...prev, semesterId]
    );
  };

  const selectAllSemesters = () => setSelectedSemesters(semesters.map((s) => s.id));
  const deselectAllSemesters = () => setSelectedSemesters([]);

  const getFilteredGradesForChart = () => {
    if (selectedSemesters.length === 0) return [];
    return grades.filter((grade) => {
      if (grade.semesterId && selectedSemesters.includes(grade.semesterId)) return true;
      const semId = typeof grade.semester === "object" ? grade.semester.id : grade.semesterId;
      return semId && selectedSemesters.includes(semId);
    });
  };

  const semestersBySchool = useMemo(() => {
    const grouped = {};
    semesters.forEach((sem) => {
      const schoolId = sem.schoolId || "other";
      if (!grouped[schoolId]) {
        grouped[schoolId] = {
          schoolId,
          schoolName:
            sem.schoolName || sem.school?.name || (sem.schoolId ? `School ${sem.schoolId}` : "General"),
          semesters: [],
        };
      }
      grouped[schoolId].semesters.push(sem);
    });
    return Object.values(grouped);
  }, [semesters]);

  const filteredGrades = useMemo(() => {
    let data = grades;

    if (selectedSemester !== "ALL") {
      data = data.filter(
        (g) => g.semester === selectedSemester || g.semester?.name === selectedSemester
      );
    }
    if (selectedSubjectFilter !== "ALL") {
      data = data.filter((g) => String(g.subjectId) === String(selectedSubjectFilter));
    }

    return data;
  }, [grades, selectedSemester, selectedSubjectFilter]);

  const subjectAverages = useMemo(() => {
    const subjectMap = {};
    filteredGrades.forEach((grade) => {
      if (!subjectMap[grade.subjectId]) subjectMap[grade.subjectId] = [];
      subjectMap[grade.subjectId].push(grade);
    });

    return Object.keys(subjectMap)
      .map((subjectId) => {
        const subject = subjects.find((s) => String(s.id) === String(subjectId));
        const avg = calculateGradesAverage(subjectMap[subjectId]);
        return {
          subjectId,
          subjectName: subject ? subject.name : "Unknown",
          average: avg,
          count: subjectMap[subjectId].length,
        };
      })
      .sort((a, b) => b.average - a.average);
  }, [filteredGrades, subjects]);

  const overallAverage = useMemo(() => {
    // Use the standardized calculation function to ensure consistency across the app
    // This calculates the average of subject averages
    return calculateOverallAverage(filteredGrades);
  }, [filteredGrades]);

  const totalGrades = filteredGrades.length;

  // const bestSubject = subjectAverages.length > 0 ? subjectAverages[0] : null;
  const atRiskSubjects = subjectAverages.filter((s) => s.average < 4.0);

  const customAverage = useMemo(() => {
    return calculateAverage(
      grades,
      customSelectedSubjects,
      selectedSemester === "ALL" ? null : selectedSemester
    );
  }, [grades, customSelectedSubjects, selectedSemester]);

  const handleSubjectToggle = (subjectIds) => {
    // Handle array of IDs (for grouped subjects) or single ID
    const idsToToggle = Array.isArray(subjectIds) ? subjectIds : [subjectIds];
    
    setCustomSelectedSubjects((prev) => {
      // Check if all these IDs are already selected
      const allSelected = idsToToggle.every(id => prev.includes(id));
      
      if (allSelected) {
        // Deselect all
        return prev.filter(id => !idsToToggle.includes(id));
      } else {
        // Select all (add missing ones)
        const newIds = idsToToggle.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      }
    });
  };

  // Group subjects by school and then by name for the custom calculator
  const subjectsBySchool = useMemo(() => {
    const filtered = subjects.filter(s => 
      s.name.toLowerCase().includes(subjectSearchTerm.toLowerCase())
    );
    
    const grouped = {};
    filtered.forEach(subject => {
      const schoolName = subject.schoolName || 'General';
      if (!grouped[schoolName]) {
        grouped[schoolName] = {};
      }
      
      // Group by subject name to combine same subjects across semesters
      if (!grouped[schoolName][subject.name]) {
        grouped[schoolName][subject.name] = {
          name: subject.name,
          ids: [],
          originalSubject: subject
        };
      }
      grouped[schoolName][subject.name].ids.push(subject.id);
    });
    
    // Convert to array format for rendering
    const result = {};
    Object.keys(grouped).forEach(school => {
      result[school] = Object.values(grouped[school]);
    });
    
    return result;
  }, [subjects, subjectSearchTerm]);

  if (loading) return <LoadingSpinner />;

  return (
    <div >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-chart-pie text-sunrise-red mr-4"></i>
                  Grade Analytics
                </h1>
                <p className="text-gray-600">
                  Track your performance across subjects and semesters.
                </p>
              </div>

              {/* Small filters row */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setIsSemesterFilterOpen(true)}
                  className="btn-secondary flex items-center justify-center gap-2"
                  title="Filter semesters for the chart"
                >
                  <i className="fas fa-filter"></i>
                  <span>Semesters ({selectedSemesters.length})</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Metric cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card-brand overflow-hidden">
            <div className="bg-green-cloud text-white p-4">
              <h3 className="text-sm font-semibold flex items-center">
                <i className="fas fa-signal mr-2"></i>
                Overall Average
              </h3>
            </div>
            <div className="p-6">
              <div 
                className="text-4xl font-bold"
                style={{ color: overallAverage ? getGradeColor(overallAverage) : undefined }}
              >
                {overallAverage ? overallAverage : "-"}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Based on current filters (semester).
              </p>
            </div>
          </div>

          <div className="card-brand overflow-hidden">
            <div className="bg-gradient-to-r from-grey-dusk to-slate-cloud text-white p-4">
              <h3 className="text-sm font-semibold flex items-center">
                <i className="fas fa-layer-group mr-2"></i>
                Total Grades
              </h3>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-grey-dusk">{totalGrades}</div>
              <p className="text-xs text-gray-500 mt-2">
                Grades included in the current view.
              </p>
            </div>
          </div>

          <div className="card-brand overflow-hidden">
            <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 text-white p-4">
              <h3 className="text-sm font-semibold flex items-center">
                <i className="fas fa-triangle-exclamation mr-2"></i>
                At-risk subjects
              </h3>
            </div>
            <div className="p-6">
              <div className="text-4xl font-bold text-red-600">{atRiskSubjects.length}</div>
              <p className="text-xs text-gray-500 mt-2">
                Subjects with average below 4.0.
              </p>
            </div>
          </div>
        </div>

        {/* Chart card */}
        <div className="card-brand overflow-hidden mb-8 relative">
          <div className="bg-gradient-to-r from-grey-dusk to-slate-cloud text-white p-5 flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90 mt-1">
                {selectedSemesters.length} semester(s) selected
              </p>
            </div>

            <button
              onClick={() => setIsSemesterFilterOpen(true)}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg p-3 transition-all duration-200 flex items-center justify-center"
              title="Filter semesters"
            >
              <i className="fas fa-filter text-lg"></i>
            </button>
          </div>

          <div className="p-4 md:p-6">
            <GradesChart
              grades={getFilteredGradesForChart()}
              subjects={subjects}
              selectedSemester={null}
              hideChartOnMobile={false}
            />
          </div>

          {/* Semester Filter Modal */}
          {isSemesterFilterOpen && (
            <>
              <div
                className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[65]"
                onClick={() => setIsSemesterFilterOpen(false)}
              />
              <div className="fixed z-[70] top-24 right-4 md:right-8 w-[22rem] max-h-[28rem] overflow-y-auto card-brand shadow-brand-lg">
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-ice-white to-pale-dawn/40">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-grey-dusk flex items-center">
                      <i className="fas fa-filter mr-2 text-sunrise-red"></i>
                      Semester filter
                    </h3>
                    <button
                      onClick={() => setIsSemesterFilterOpen(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={selectAllSemesters}
                      className="flex-1 px-3 py-2 text-sm bg-grey-dusk/10 text-grey-dusk rounded-lg hover:bg-grey-dusk/20 transition-colors"
                    >
                      <i className="fas fa-check-double mr-1"></i>
                      Select all
                    </button>
                    <button
                      onClick={deselectAllSemesters}
                      className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <i className="fas fa-times-circle mr-1"></i>
                      Clear
                    </button>
                  </div>

                  {semestersBySchool.map((group) => (
                    <div key={group.schoolId} className="mb-4">
                      <div className="font-semibold text-grey-dusk mb-2 flex items-center">
                        <i className="fas fa-school mr-2 text-sunrise-red"></i>
                        {group.schoolName}
                      </div>

                      <div className="space-y-2 ml-2">
                        {group.semesters.map((sem) => {
                          const isCurrent =
                            sem.classInfo?.startDate &&
                            (() => {
                              const currentSem = getCurrentSemester(sem.classInfo.startDate);
                              if (!currentSem?.semesterNumber) return false;
                              const semesterNum = parseInt(sem.name?.match(/\d+/)?.[0] || "0");
                              return semesterNum === currentSem.semesterNumber;
                            })();

                          return (
                            <label
                              key={sem.id}
                              className="flex items-center p-2 hover:bg-ice-white rounded-lg cursor-pointer transition-colors"
                            >
                              <div className="relative flex items-center">
                                <input
                                  type="checkbox"
                                  checked={selectedSemesters.includes(sem.id)}
                                  onChange={() => toggleSemester(sem.id)}
                                  className="rounded border-gray-300 text-sunrise-red focus:ring-sunrise-red mr-3 h-4 w-4"
                                />
                                {isCurrent && (
                                  <div className="absolute -left-1 -top-1 w-2 h-2 bg-green-500 rounded-full border-2 border-white"></div>
                                )}
                              </div>

                              <div className="flex-1 flex items-center justify-between">
                                <span className="text-sm text-gray-700">
                                  {sem.name}
                                  {sem.className && (
                                    <span className="text-sunrise-red ml-1 font-medium">
                                      - {sem.className}
                                    </span>
                                  )}
                                </span>
                                {isCurrent && (
                                  <span className="ml-2 px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200">
                                    Current
                                  </span>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Bottom row: best subject + custom average */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Custom Average Calculator */}
          <div className="card-brand overflow-hidden lg:col-span-2">
            <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 text-white p-4">
              <h3 className="text-xl font-semibold flex items-center">
                <i className="fas fa-calculator mr-2"></i>
                Custom average calculator
              </h3>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Select subjects to calculate a custom average.
              </p>

              {/* Search Bar */}
              <div className="mb-6 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  placeholder="Search subjects..."
                  className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunrise-red focus:border-transparent"
                  value={subjectSearchTerm}
                  onChange={(e) => setSubjectSearchTerm(e.target.value)}
                />
              </div>

              <div className="space-y-6 mb-6">
                {Object.entries(subjectsBySchool).map(([schoolName, schoolSubjects]) => (
                  <div key={schoolName}>
                    <h4 className="text-sm font-bold text-grey-dusk uppercase tracking-wider mb-3 border-b border-gray-200 pb-1">
                      {schoolName}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {schoolSubjects.map((subjectGroup, idx) => {
                        // Check if all IDs for this subject name are selected
                        const active = subjectGroup.ids.every(id => customSelectedSubjects.includes(id));
                        
                        return (
                          <button
                            key={`${subjectGroup.name}-${idx}`}
                            onClick={() => handleSubjectToggle(subjectGroup.ids)}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                              active
                                ? "bg-sunrise-red/10 border-sunrise-red text-sunrise-red font-semibold"
                                : "bg-ice-white border-pale-dawn text-grey-dusk hover:bg-pale-dawn/40"
                            }`}
                          >
                            {subjectGroup.name}
                            {subjectGroup.ids.length > 1 && (
                              <span className="ml-2 text-xs opacity-70 bg-black/5 px-1.5 rounded-full">
                                {subjectGroup.ids.length}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                
                {Object.keys(subjectsBySchool).length === 0 && (
                  <div className="text-center py-4 text-gray-500 italic">
                    No subjects found matching "{subjectSearchTerm}"
                  </div>
                )}
              </div>

              <div className="mt-4 p-4 bg-ice-white rounded-lg border border-pale-dawn flex items-center justify-between">
                <span className="font-medium text-grey-dusk">
                  Calculated average (selected subjects)
                </span>
                <span 
                  className="text-2xl font-bold"
                  style={{ color: customAverage ? getGradeColor(customAverage) : undefined }}
                >
                  {customAverage || "-"}
                </span>
              </div>

              <div className="text-xs text-gray-500 mt-3 flex items-start">
                <i className="fas fa-info-circle mr-2 mt-0.5 text-sunrise-red"></i>
                Tip: By default, all subjects are considered unless you select a specific one above.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
